import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {Nut_Settings_User} from 'nutshell-core/settings-constants';

import {Button} from 'shells/button/button';
import {PageControlBar} from 'shells/control-bar/page-control-bar';
import {LeadIcon} from 'shells/icon/svg/lead-icon';
import {TripleChevronIcon} from 'shells/icon/svg/triple-chevron-icon';
import {Stack} from 'shells/layout/stack/stack';
import {Link} from 'shells/link/link';
import {UserActionTimestamp} from 'shells/timestamp/user-action-timestamp';

import type {SalesAutomationStageset as SalesAutomationStagesetFragment} from 'nutshell-graphql-types';

import {useAugmentTabWithNewFeatureNotification} from '../../../core/graphql/hooks/use-augment-tab-with-new-feature-notification';
import {CustomizeLayoutButton} from '../../../core-entity/layout-customizer/customize-layout-button';
import {PageTitleBar} from '../../../layout';
import {useGetLimitsByType} from '../../../limits';
import {OpenableModals, useOpenableModal} from '../../../openable-modals';

import {ActivateDraftPipelineConfirmationModal} from '../../home/activate-draft-pipeline-confirmation-modal';
import {getPipelineStatusBadge, getLeadsListLink} from '../../home/helpers';

const OPEN_LEADS_TAB = 'open-leads';
const FOLLOW_THROUGH_TAB = 'closed-leads';

type Props = {
    stageset: SalesAutomationStagesetFragment;
};

function getBasePath(): string {
    return `/sales/pipelines`;
}

export function SalesAutomationAppHeader(props: Props) {
    const [isActivatePipelineModalOpen, setIsActivatePipelineModalOpen] =
        React.useState<boolean>(false);

    const basePath = getBasePath();
    const modifiedTime = props.stageset.modifiedTime;
    const modifiedUser = props.stageset.modifiedUser;

    const {limits} = useGetLimitsByType('PIPELINES');
    const {onOpen: openFeatureGateModal} = useOpenableModal(
        OpenableModals.PIPELINES_FEATURE_GATE_MODAL
    );

    const followThroughTab = {
        id: FOLLOW_THROUGH_TAB,
        label: 'Follow-through',
        icon: TripleChevronIcon,
        href: `${basePath}/${props.stageset.id}/closed-leads`,
    } as const;
    const augmentedTab = useAugmentTabWithNewFeatureNotification(
        followThroughTab,
        Nut_Settings_User.SETTING_HAS_VIEWED_SALES_AUTOMATION_LEAD_FOLLOWUP
    );

    const isStagesetDraft =
        (props.stageset.status === 'DRAFT' || props.stageset.status === 'RETIRED') &&
        !props.stageset.isArchived;

    const buttons = React.useMemo(() => {
        if (!NutClientConfig.hasDraftPipelinesFF && !NutClientConfig.hasLeadPageRedesignM3FF) {
            return undefined;
        }

        return (
            <Stack spacing={8} isHorizontal={true} justifyContent='space-between'>
                {NutClientConfig.hasLeadPageRedesignM3FF && (
                    <CustomizeLayoutButton
                        text='Customize lead page layout'
                        entityType='LEAD'
                        stagesetId={props.stageset.id}
                    />
                )}
                {NutClientConfig.hasDraftPipelinesFF && isStagesetDraft && (
                    <>
                        <Button
                            variant='primary'
                            size='big'
                            onClick={() => {
                                if (limits && limits.canAdd) {
                                    setIsActivatePipelineModalOpen(true);
                                } else {
                                    openFeatureGateModal();
                                }
                            }}
                        >
                            Activate pipeline
                        </Button>
                        {isActivatePipelineModalOpen && (
                            <ActivateDraftPipelineConfirmationModal
                                stagesetId={props.stageset.id}
                                stagesetName={props.stageset.name}
                                onClose={() => setIsActivatePipelineModalOpen(false)}
                            />
                        )}
                    </>
                )}
            </Stack>
        );
    }, [
        isActivatePipelineModalOpen,
        limits,
        openFeatureGateModal,
        props.stageset.id,
        props.stageset.name,
        isStagesetDraft,
    ]);

    return (
        <>
            <PageTitleBar
                title={props.stageset.name}
                subheaderPrimaryText=''
                breadcrumbs={[
                    {
                        text: 'Pipelines',
                        to: `${basePath}?tab=${isStagesetDraft ? 'draft' : 'active'}`,
                    },
                ]}
                buttons={buttons}
            />
            <PageControlBar
                tabs={{
                    selectedTab: getActiveTab(window.location.pathname),
                    options: [
                        {
                            id: OPEN_LEADS_TAB,
                            label: 'Open leads',
                            icon: LeadIcon,
                            href: `${basePath}/${props.stageset.id}`,
                        },
                        // @ts-expect-error
                        augmentedTab,
                    ],
                }}
                statusInfo={{
                    primaryText: '',
                    secondaryText: (
                        <Stack spacing={0} justifyContent='center'>
                            <Link
                                // @ts-expect-error
                                as={RoutedLink}
                                to={getLeadsListLink(props.stageset.id)}
                                variant='primary'
                            >
                                {`${props.stageset.numOfOpenLeads} ${
                                    props.stageset.numOfOpenLeads === 1 ? 'open lead' : 'open leads'
                                }`}
                            </Link>
                            {modifiedUser && modifiedTime && (
                                <UserActionTimestamp
                                    format='relative'
                                    user={modifiedUser}
                                    modifiedTime={modifiedTime}
                                />
                            )}
                        </Stack>
                    ),
                    // @ts-expect-error
                    status: NutClientConfig.hasDraftPipelinesFF
                        ? {
                              ...getPipelineStatusBadge(
                                  props.stageset.status,
                                  props.stageset.default,
                                  props.stageset.isArchived
                              ),
                          }
                        : undefined,
                }}
            />
        </>
    );
}

function getActiveTab(pathname: string): string {
    return pathname.includes(FOLLOW_THROUGH_TAB) ? FOLLOW_THROUGH_TAB : OPEN_LEADS_TAB;
}
